import stableObjectHash from 'stable-hash';
import xxhash from "xxhash-wasm";

const xxHasher = await xxhash();

// This is sha256('flakiness.io').substring(0, 10)
const ID_SEPARATOR = `388c03a1c6`;

export function createAbortPromise(signal: AbortSignal): { promise: Promise<void>, [Symbol.dispose]: () => void } {
  if (signal.aborted)
    return { promise: Promise.resolve(), [Symbol.dispose]: () => {} };
  let abortListener: (this: AbortSignal, ev: Event) => any;
  const promise = new Promise<void>(resolve => {
    abortListener = () => resolve();
    signal.addEventListener('abort', abortListener, { once: true });
  });
  return {
    promise,
    [Symbol.dispose]: () => signal.removeEventListener('abort', abortListener),
  }
}

export function mb(bytes: number) {
  const suffixes = ['B', 'KB', 'MB', 'GB'];
  for (const suffix of suffixes) {
    if (bytes < 1024)
      return bytes.toFixed(2) + suffix;
    bytes /= 1024;
  }
  return bytes.toFixed(2) + 'PB';
}

export function humanReadableMs(ms: number | { valueOf(): number }): string {
  if (+ms < 1e-5)
    return `0 ms`;
  if (+ms < 1)
    return `${Math.round(+ms * 1000)} µs`
  ms = Math.round(+ms);
  let seconds = ((+ms) / 1000);
  if (seconds < 1)
    return `${ms} ms`;
  if (seconds < 60)
    return `${seconds.toFixed(1)} sec`;
  seconds = Math.round(seconds);
  let minutes = (seconds / 60)|0;
  seconds = seconds % 60;
  if (minutes < 1)
    return `${seconds} sec`;
  let hours = (minutes / 60)|0;
  minutes = minutes % 60;
  if (hours < 1)
    return seconds !== 0 ? `${minutes} min ${seconds} sec` : `${minutes} min`;
  let days = (hours / 24)|0;
  if (days < 1)
    return `${hours} h ${minutes} min`;
  hours = hours % 24;
  let weeks = (days / 7)|0;
  if (weeks < 1)
    return `${days} days ${hours} h ${minutes} min`;
  days %= 7;
  return `${weeks} weeks ${days} days ${hours} h`;
}

export function durationTrendScale(currentMs: number, baseMs: number): string {
  const trend = baseMs > 0 ? currentMs / baseMs : 1;
  if (trend > 1000)
    return '>x1000';
  if (trend > 2)
    return `x${trend.toFixed(2)}`;
  if (trend > 1.01)
    return `+${(trend - 1) * 100 | 0}%`;
  if (trend > 0.99)
    return '±0%';
  return `-${(1 - trend) * 100 | 0}%`;
}

export function humanReadableBytes(bytes: number): string {
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let i = 0;
  while (bytes >= 1000 && i < units.length - 1) {
    bytes /= 1000;
    i++;
  }
  // For small integer values, we care about float part.
  // For others, we only care about integer part.
  let rounded = bytes.toFixed(1);
  if (Math.round(bytes) > 99 || bytes === 0)
    rounded = Math.round(bytes) + '';
  return `${rounded} ${units[i]}`;
}

export function humanReadableInteger(value: number): string {
  const units = ["", "K", "M", "B", "T"];
  let i = 0;
  while (value >= 1000 && i < units.length - 1) {
    value /= 1000;
    i++;
  }
  if (i === 0)
    return `${Math.floor(value)}`;
  // Truncate to 1 decimal place (no rounding) to avoid 999.95 → "1000.0K".
  const truncated = Math.floor(value * 10) / 10;
  if (truncated % 1 === 0)
    return `${truncated}${units[i]}`;
  return `${truncated.toFixed(1)}${units[i]}`;
}

export async function raceWithSignal<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal)
    return await promise;
  using abortPromise = createAbortPromise(signal);
  return await Promise.race([
    abortPromise.promise,
    promise,
  ]) as Promise<T>;
}

export function xxHash(data: string | string[]): string {
  if (Array.isArray(data))
    return xxHasher.h64ToString(data.join(ID_SEPARATOR));
  return xxHasher.h64ToString(data);
}

export function xxHashBigint(data: string | string[]): bigint {
  if (Array.isArray(data))
    return xxHasher.h64(data.join(ID_SEPARATOR));
  return xxHasher.h64(data);
}

export function xxHashObjectBigint(obj: any): bigint {
  return xxHashBigint(stableObjectHash(obj));
}

export function xxHashObject(obj: any): string {
  return xxHash(stableObjectHash(obj));
}

/**
 * Brands a type by intersecting it with a type with a brand property based on
 * the provided brand string.
 */
export type Brand<T, Brand extends string> = T & {
  readonly [B in Brand as `__${B}_brand`]: never;
};

export function randomUUID(): string {
  return crypto.randomUUID();
}

export function CHECK(value: unknown, message?: string): asserts value {
  if (!value)
    throw new Error(`Assertion failed ${message ?? ''}`.trim())
}

export function randomUUIDBase62(): string {
  const BASE62_CHARSET = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let num = BigInt('0x' + randomUUID().replace(/-/g, ''));
  if (num === 0n)
    return BASE62_CHARSET[0];

  const chars = [];
  while (num > 0n) {
    const remainder = Number(num % 62n);
    num /= 62n;
    chars.push(BASE62_CHARSET[remainder]);
  }
  
  return chars.reverse().join('');
}

export async function retryUntilDeadline<T>(operation: () => T, deadline: number): Promise<T> {
  const timeouts = [100, 200, 500, 500];
  const defaultTimeout = 1000;
  let lastError;
  while (Date.now() < deadline) {
    try {
      return await operation();
    } catch (e) {
      lastError = e;
      await new Promise(x => setTimeout(x, timeouts.shift() ?? defaultTimeout));
    }
  }
  throw lastError;
}

/**
 * Yields map(element) for each element in input order, running at most concurrency calls at once.
 */
export async function *bufferedMap<I, O>(elements: I[], map: (i: I, signal?: AbortSignal) => Promise<O>, concurrency: number, signal?: AbortSignal) {
  const controller = new AbortController();
  const innerSignal = signal ? AbortSignal.any([signal, controller.signal]) : controller.signal;
  const safeMap = (i: I) => Promise.resolve().then(() => map(i, innerSignal)).then(result => ({ result })).catch(error => ({ error }));
  const inflights = elements.slice(0, concurrency).map(safeMap);
  try {
    for (let i = 0; i < elements.length; ++i) {
      signal?.throwIfAborted();
      const result = await inflights.shift()!;
      if ('error' in result)
        throw result.error;
      if (i + concurrency < elements.length)
        inflights.push(safeMap(elements[i + concurrency]));
      yield result.result;
    }
  } finally {
    // Runs when the consumer breaks out early or something throws: cancel the
    // calls still in flight, since nobody will read their results.
    controller.abort();
  }
}

interface Measure {
  (title: string): void; // Just an example
  reset(): void;
  disableLog(): void;
  timestamp(): number;
  duration(): number;
}

export function measure(prefix?: string) {
  let timestamp = Date.now();
  if (prefix)
    prefix = prefix.trim() + ' ';
  else 
    prefix = '';

  let logEnabled = true;
  const m: Measure = (title: string) => {
    if (!logEnabled)
      return;
    const suffix = ': ' + (Date.now() - timestamp) + 'ms';
    console.log(prefix + title + suffix);
    timestamp = Date.now();
  }
  m.reset = () => {
    timestamp = Date.now();
  }
  m.disableLog = () => {
    logEnabled = false;
  }
  m.timestamp = () => timestamp;
  m.duration = () => Date.now() - timestamp;

  return m;
}


